import React from 'react';
import styled from 'styled-components';
import type {
    DocumentOcrExcerpt,
    AdjacentDocumentContext,
    DocumentReviewStatus,
} from '../../types';
import { DocumentInspectHeader, type DocumentInspectHeaderProps } from './DocumentInspectHeader';
import {
    DocumentPageRail,
    type DocumentPageRailProps,
    type DocumentPageRailInteractionEvent,
    type SelectionPillConfig,
} from './DocumentPageRail';
import { computeUndoAnchor, formatPageRanges, parsePageRanges } from '../../utils/pageRanges';
import { PageMovePill } from './PageMovePill';
import { withPageImageWidth } from '../../utils/pageImages';
import { DocumentCanvasPanel, type CanvasPage, type PageActionEntry, type ViewerMode } from './DocumentCanvasPanel';
import { DocumentInspectToolbar } from './DocumentInspectToolbar';
import type { DuplicatePairItem } from '../DuplicatesModal';
import {
    ModalOverlay,
    ModalPanel,
    ModalTitle,
    ModalBody,
    ModalInput,
    ModalActions,
    ModalButton,
    ModalLabel,
} from '../shared/Modal';

export interface DocumentPageActions {
    /** When true, all items in the menu are disabled (e.g., during an
     *  in-flight bulk action). Individual items are also disabled based on
     *  whether their target (selection, etc.) exists. */
    disabled?: boolean;
    /** Remove the given pages from the current document. */
    onDeletePages: (pageIds: string[]) => void;
    /** Split the current document after `anchorPageId`: pages 1..anchor stay,
     *  pages after anchor move into a brand-new document inserted right after. */
    onSplitAfter: (anchorPageId: string, options: { firstDocTitle: string; secondDocTitle: string }) => void;
    /** Export the current document as a PDF. */
    onExportDocument?: () => void;
    /** Open the Re-run analysis modal for the current document. */
    onReprocessDocument?: () => void;
}

export interface DocumentStatusConfig {
    // DERIVED, read-only. Shown as a badge in the toolbar; there is no
    // onStatusChange — status follows from confirming title/date/category.
    status: DocumentReviewStatus;
}

export interface DocumentInspectWorkspaceProps {
    header: DocumentInspectHeaderProps;
    /** DERIVED review status (read-only badge in the toolbar). */
    documentStatus: DocumentStatusConfig;
    pageRail: DocumentPageRailProps;
    pages: CanvasPage[];
    ocrExcerpts?: DocumentOcrExcerpt[];
    /** Current search query (shared with the case-level search). The
     *  toolbar's search input writes back through `onSearchQueryChange`. */
    searchQuery?: string;
    onSearchQueryChange?: (value: string) => void;
    previousDocument?: AdjacentDocumentContext;
    nextDocument?: AdjacentDocumentContext;
    onMergeAdjacent?: (adjacentDocId: string, direction: 'before' | 'after') => void;
    onNavigateToDocument?: (docId: string) => void;
    duplicateFilterDocId?: string;
    onClearDuplicateFilter?: () => void;
    duplicateData?: DuplicatePairItem[] | null;
    onNavigateToDuplicate?: (docId: string, pageNumber?: number) => void;
    getThumbnailUrl?: (pageId: string) => string | null;
    /** Page-level actions surfaced in the toolbar's Actions menu. */
    pageActions?: DocumentPageActions;
    /** Replace the current selection wholesale (used by the pill commit). */
    onSetSelection?: (pageIds: string[]) => void;
    /** Toggle a single page's selection (or extend a range when extend=true). */
    onToggleSelect?: (pageId: string, extend: boolean) => void;
    /** Reorder pages within the current document by drag-and-drop on the rail.
     *  `pageIds` are the pages to move (already the contiguous chunk to gather);
     *  they land immediately before/after `anchorPageId` per `position`. */
    onReorderPages?: (
        pageIds: string[],
        anchorPageId: string,
        position: 'before' | 'after',
    ) => void;
    /** Undo a reorder: move `pageIds` back to their original position — after
     *  `afterPageId`, or to the document start when `afterPageId` is null. */
    onUndoReorder?: (pageIds: string[], afterPageId: string | null) => void;
}

// Compact rail: wide enough for a 44px thumb + hover checkbox + number.
const MIN_RAIL_WIDTH = 76;
const MAX_RAIL_WIDTH = 480;
const COLLAPSED_RAIL_WIDTH = 32;

// Zoom bounds/step shared by the toolbar cluster and the canvas.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.1;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    position: relative; /* anchor for the floating move/undo pill */
`;

// Floating "pages moved" pill: bottom-centre over the workspace, above the rail
// and canvas, out of the way of the toolbar.
const MovePillAnchor = styled.div`
    position: absolute;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    z-index: 20;
    pointer-events: none; /* let the pill itself capture clicks, not the wrapper */

    & > * {
        pointer-events: auto;
    }
`;

const MainArea = styled.div<{ $railWidth: number }>`
    display: grid;
    grid-template-columns: ${({ $railWidth }) => `${Math.round($railWidth)}px 12px 1fr`};
    gap: 16px;
    align-items: stretch;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
`;

const RailContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    min-width: 0;
    overflow: visible;
`;

const ViewerColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* Stretch the canvas to the column's full width so the image pane fills the
       available space and the page image centers within it. */
    align-items: stretch;
    height: 100%;
    min-height: 0;
    /* Scroll the page viewer when the header info + page image are taller than
       the available height (small screens / large headers) instead of clipping
       it with no way to reach the bottom. */
    overflow-y: auto;
`;

const CanvasWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1 1 auto;
    min-height: 0;
`;

const Resizer = styled.div<{ $dragging: boolean }>`
    width: 12px;
    height: 100%;
    cursor: col-resize;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        width: 2px;
        height: 32px;
        border-radius: 999px;
        background: ${({ $dragging }) => ($dragging ? '#999' : '#ddd')};
        transition: background 0.15s ease;
    }

    &:hover::before {
        background: #999;
    }

    &:focus-visible::before {
        background: #2563eb;
    }
`;

const filterPages = (pages: DocumentPageRailProps['pages'], query: string) => {
    if (!query) {
        return pages;
    }
    const lowered = query.trim().toLowerCase();
    if (!lowered) {
        return pages;
    }

    return pages.filter(page => {
        const numberMatch = page.pageNumber.toString().includes(lowered);
        const previewMatch = page.ocrPreview?.toLowerCase().includes(lowered);
        const notesMatch = page.notes?.some(note => note.toLowerCase().includes(lowered));
        const warningMatch = page.warnings?.some(warning => warning.label.toLowerCase().includes(lowered));
        const labelMatch = page.label?.toLowerCase().includes(lowered);
        const boundaryMatch = page.boundaryAfter?.confidence != null
            ? `${Math.round(page.boundaryAfter.confidence * 100)}`.includes(lowered)
            : false;
        return numberMatch || previewMatch || notesMatch || warningMatch || labelMatch || boundaryMatch;
    });
};

export const DocumentInspectWorkspace: React.FC<DocumentInspectWorkspaceProps> = ({
    header,
    documentStatus,
    pageRail,
    pages,
    ocrExcerpts,
    searchQuery,
    onSearchQueryChange,
    previousDocument,
    nextDocument,
    onMergeAdjacent,
    onNavigateToDocument,
    duplicateFilterDocId,
    onClearDuplicateFilter,
    duplicateData,
    onNavigateToDuplicate,
    getThumbnailUrl,
    pageActions,
    onSetSelection,
    onToggleSelect,
    onReorderPages,
    onUndoReorder,
}) => {
    const [railWidth, setRailWidth] = React.useState<number>(MIN_RAIL_WIDTH);
    const [railCollapsed, setRailCollapsed] = React.useState<boolean>(false);
    // Viewer mode (Image / Text / Side-by-Side) used to live in the canvas
    // panel — moved up so the toolbar can drive it.
    const [viewerMode, setViewerMode] = React.useState<ViewerMode>('image');
    // `viewingPageId` decouples canvas navigation from selection. A thumbnail
    // click sets it; checkbox toggles do not. Falls back to the first selected
    // page when null.
    const [viewingPageId, setViewingPageId] = React.useState<string | null>(null);
    // The page the continuous-scroll viewport is currently on (reported by the
    // canvas stack). Drives the rail highlight / meta strip / OCR pane, while
    // `viewingPageId` remains the explicit navigation TARGET.
    const [viewportPageId, setViewportPageId] = React.useState<string | null>(null);
    // Bumped on every explicit nav so the stack re-scrolls even when the
    // target page id is unchanged (e.g. re-clicking the current rail entry).
    const [scrollToken, setScrollToken] = React.useState(0);
    // Zoom lives here so the toolbar cluster controls it and the canvas
    // renders it (the old floating canvas control is gone in this view).
    const [zoom, setZoom] = React.useState(1);
    const zoomBy = React.useCallback((delta: number) => {
        setZoom(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 100) / 100)));
    }, []);
    const [isResizing, setIsResizing] = React.useState(false);
    // Snapshot of the delete/split target taken at the moment the menu item
    // was clicked. Subsequent selection changes don't mutate an open modal —
    // that would be confusing.
    const [deleteRequest, setDeleteRequest] = React.useState<{
        pageIds: string[];
        pageNumbers: number[];
    } | null>(null);
    const [splitRequest, setSplitRequest] = React.useState<{
        anchorPageId: string;
        anchorPageNumber: number;
        firstTitle: string;
        secondTitle: string;
    } | null>(null);
    // Bad text from the last pill commit. When set, the pill renders in
    // error styling with this text; cleared once the user commits something
    // valid or clears the pill.
    const [invalidPillText, setInvalidPillText] = React.useState<string | null>(null);
    // Ephemeral "pages moved" pill after a reorder: shows what moved and offers
    // Undo (move the chunk back after its original predecessor) for 15s. `nonce`
    // remounts the pill/countdown on each new move so a fresh drag restarts the
    // timer instead of riding the previous one.
    const [movePill, setMovePill] = React.useState<{
        label: string;
        pageIds: string[];
        undoAfterId: string | null;
        nonce: number;
    } | null>(null);

    React.useEffect(() => {
        setViewingPageId(null);
        setViewportPageId(null);
        setScrollToken(0);
        setDeleteRequest(null);
        setSplitRequest(null);
        setInvalidPillText(null);
        setMovePill(null);
    }, [header.documentId]);

    const allPages = pageRail.pages;
    const selectedCount = React.useMemo(() => allPages.filter(page => page.selected).length, [allPages]);

    // Clear the invalid pill state if the selection becomes empty by any means
    // other than an invalid commit (e.g., checkbox toggles, programmatic).
    React.useEffect(() => {
        if (selectedCount === 0 && invalidPillText == null) {
            // no-op — pill is already hidden
        }
        // If checkboxes were used to (re)build a real selection, invalidate the
        // bad-pill memory; the new selection is the source of truth.
        if (selectedCount > 0 && invalidPillText != null) {
            setInvalidPillText(null);
        }
    }, [selectedCount, invalidPillText]);

    const filteredPages = React.useMemo(() => {
        // `searchQuery` is shared with the case-level search. When set, the
        // backend has already populated `ocrPreview` on matching pages via
        // fetchDocumentDetail — `filterPages` matches against that plus
        // page number, label, notes, warnings, and boundary confidence.
        let pages = filterPages(allPages, searchQuery ?? '');
        if (duplicateFilterDocId && duplicateData) {
            const currentDocId = header.documentId;
            if (duplicateFilterDocId === currentDocId) {
                // Case 1: viewing the target doc — show pages with any external duplicate
                const pagesWithExternalDups = new Set(
                    duplicateData
                        .filter(p => p.left.doc_id !== p.right.doc_id)
                        .flatMap(p => [
                            p.left.doc_id === currentDocId ? p.left.page_id : null,
                            p.right.doc_id === currentDocId ? p.right.page_id : null,
                        ])
                        .filter(Boolean) as string[]
                );
                pages = pages.filter(p => pagesWithExternalDups.has(p.id));
            } else {
                // Case 2: viewing a related doc — show pages that match the filter target
                const pagesMatchingTarget = new Set(
                    duplicateData
                        .filter(p =>
                            (p.left.doc_id === duplicateFilterDocId && p.right.doc_id === currentDocId) ||
                            (p.right.doc_id === duplicateFilterDocId && p.left.doc_id === currentDocId)
                        )
                        .map(p => p.left.doc_id === currentDocId ? p.left.page_id : p.right.page_id)
                );
                pages = pages.filter(p => pagesMatchingTarget.has(p.id));
            }
        }
        return pages;
    }, [allPages, searchQuery, duplicateFilterDocId, duplicateData, header.documentId]);

    const visibleActivePageId = React.useMemo(() => {
        // Explicit thumbnail click (current doc or adjacent) wins.
        if (viewingPageId) {
            return viewingPageId;
        }
        const selectedVisible = filteredPages.find(page => page.selected);
        if (selectedVisible) {
            return selectedVisible.id;
        }
        if (filteredPages.length > 0) {
            return filteredPages[0].id;
        }
        const selectedAny = allPages.find(page => page.selected);
        if (selectedAny) {
            return selectedAny.id;
        }
        const fallback = allPages[0];
        return fallback ? fallback.id : null;
    }, [filteredPages, allPages, viewingPageId]);

    // The page the UI treats as "current": the scroll viewport's page when the
    // continuous stack has reported one, otherwise the nav/selection fallback.
    const currentPageId = viewportPageId ?? visibleActivePageId;

    const totalPages = allPages.length;

    const ocrByPage = React.useMemo(() => {
        const map = new Map<number, string>();
        (ocrExcerpts ?? []).forEach(excerpt => {
            if (!map.has(excerpt.pageNumber) && excerpt.snippet) {
                map.set(excerpt.pageNumber, excerpt.snippet);
            }
        });
        return map;
    }, [ocrExcerpts]);

    const activePage = React.useMemo((): CanvasPage | null => {
        if (!currentPageId) {
            return null;
        }
        const found = pages.find(page => page.id === currentPageId);
        if (found) return found;
        // Check adjacent documents for this page ID
        const allAdjacent = [
            ...(previousDocument?.pages ?? []),
            ...(nextDocument?.pages ?? []),
        ];
        const adjPage = allAdjacent.find(p => p.pageId === currentPageId);
        if (adjPage && adjPage.pageIndex != null) {
            // Upgrade the rail thumb URL to reading resolution: identity URLs
            // switch their w bucket; legacy URLs swap the /thumb/ endpoint.
            const thumbUrl = adjPage.thumbnailUrl;
            const imageUrl = thumbUrl
                ? (thumbUrl.includes('/img?')
                    ? withPageImageWidth(thumbUrl, 680)
                    : thumbUrl.replace('/thumb/', '/full/'))
                : undefined;
            return {
                id: adjPage.pageId,
                label: `Page ${adjPage.pageNumber}`,
                pageNumber: adjPage.pageNumber,
                imageUrl,
            };
        }
        return null;
    }, [pages, currentPageId, previousDocument, nextDocument]);

    const activeOcrText = React.useMemo(() => {
        if (!activePage) {
            return null;
        }
        return ocrByPage.get(activePage.pageNumber) ?? null;
    }, [activePage, ocrByPage]);

    const selectionVisible = React.useMemo(() => filteredPages.some(page => page.selected), [filteredPages]);

    const displayedPages = React.useMemo(() => {
        if (selectionVisible || !currentPageId) {
            return filteredPages;
        }
        return filteredPages.map(page =>
            page.id === currentPageId ? { ...page, highlighted: true } : page,
        );
    }, [filteredPages, selectionVisible, currentPageId]);

    // Selection snapshot, ordered by position within the current document.
    const orderedSelectedIds = React.useMemo(
        () => allPages.filter(p => p.selected).map(p => p.id),
        [allPages],
    );
    const orderedSelectedPageNumbers = React.useMemo(
        () => allPages.filter(p => p.selected).map(p => p.pageNumber),
        [allPages],
    );
    const lastSelectedIndex = React.useMemo(() => {
        for (let i = allPages.length - 1; i >= 0; i -= 1) {
            if (allPages[i].selected) return i;
        }
        return -1;
    }, [allPages]);

    // Openers snapshot the relevant page list into the request state so the
    // modal isn't affected by subsequent selection changes.
    const openDeleteModal = React.useCallback(() => {
        if (orderedSelectedIds.length === 0) return;
        setDeleteRequest({
            pageIds: orderedSelectedIds,
            pageNumbers: orderedSelectedPageNumbers,
        });
    }, [orderedSelectedIds, orderedSelectedPageNumbers]);

    const openSplitModal = React.useCallback(() => {
        if (lastSelectedIndex < 0) return;
        const anchor = allPages[lastSelectedIndex];
        // Default the second-doc title to "<current title> (split)" so the
        // user can edit before confirming. First doc keeps current title.
        setSplitRequest({
            anchorPageId: anchor.id,
            anchorPageNumber: anchor.pageNumber,
            firstTitle: header.documentTitle,
            secondTitle: `${header.documentTitle} (split)`,
        });
    }, [allPages, lastSelectedIndex, header.documentTitle]);

    // "Split after CURRENT page" anchors on the page the viewport is on (no
    // selection needed) — the common flow when scrolling through a document
    // and spotting a boundary the model missed.
    const currentPageIndex = React.useMemo(
        () => (currentPageId ? allPages.findIndex(p => p.id === currentPageId) : -1),
        [allPages, currentPageId],
    );

    const openSplitAfterCurrentModal = React.useCallback(() => {
        if (currentPageIndex < 0) return;
        const anchor = allPages[currentPageIndex];
        setSplitRequest({
            anchorPageId: anchor.id,
            anchorPageNumber: anchor.pageNumber,
            firstTitle: header.documentTitle,
            secondTitle: `${header.documentTitle} (split)`,
        });
    }, [allPages, currentPageIndex, header.documentTitle]);

    const canvasPageActions = React.useMemo(() => {
        if (!pageActions) return undefined;

        const selectedCountLabel = selectedCount === 1 ? '' : 's';
        const items: PageActionEntry[] = [
            // Document-level actions
            {
                key: 'export',
                label: 'Export PDF',
                disabled: !pageActions.onExportDocument,
                onSelect: () => pageActions.onExportDocument?.(),
            },
            {
                key: 'reprocess',
                label: 'Re-run analysis…',
                disabled: !pageActions.onReprocessDocument,
                onSelect: () => pageActions.onReprocessDocument?.(),
            },
            // Merge with the adjacent documents (moved out of the page rail).
            {
                key: 'merge-prev',
                label: 'Merge with previous document',
                parenthetical: previousDocument?.title ?? undefined,
                disabled: !onMergeAdjacent || !previousDocument,
                disabledHint: 'No previous document',
                onSelect: () => {
                    if (previousDocument) {
                        onMergeAdjacent?.(previousDocument.docId, 'before');
                    }
                },
            },
            {
                key: 'merge-next',
                label: 'Merge with next document',
                parenthetical: nextDocument?.title ?? undefined,
                disabled: !onMergeAdjacent || !nextDocument,
                disabledHint: 'No next document',
                onSelect: () => {
                    if (nextDocument) {
                        onMergeAdjacent?.(nextDocument.docId, 'after');
                    }
                },
            },
            { key: 'sep-1', separator: true },
            // Page actions, ordered: split after current page, split after
            // selected page(s), delete selected page(s).
            {
                key: 'split-current',
                label: 'Split document after current page',
                parenthetical: currentPageIndex >= 0
                    ? `p. ${allPages[currentPageIndex].pageNumber}`
                    : undefined,
                disabled: currentPageIndex < 0 || currentPageIndex >= allPages.length - 1,
                disabledHint: currentPageIndex < 0
                    ? 'No current page'
                    : 'No pages remain after the current page',
                onSelect: openSplitAfterCurrentModal,
            },
            {
                key: 'split-selected',
                label: 'Split document after selected page',
                parenthetical: lastSelectedIndex >= 0
                    ? `p. ${allPages[lastSelectedIndex].pageNumber}`
                    : undefined,
                disabled: lastSelectedIndex < 0 || lastSelectedIndex >= allPages.length - 1,
                disabledHint: lastSelectedIndex < 0
                    ? 'No pages selected'
                    : 'No pages remain after the selected page',
                onSelect: openSplitModal,
            },
            {
                key: 'delete-selected',
                label: `Delete selected page${selectedCountLabel}`,
                tone: 'danger',
                parenthetical: selectedCount > 0 ? `${selectedCount}` : undefined,
                disabled: selectedCount === 0,
                disabledHint: 'No pages selected',
                onSelect: openDeleteModal,
            },
            // Rotate is hidden until the backend rotation path lands. Restore
            // by uncommenting this entry (the icon and handler plumbing remain
            // in place in DocumentCanvasPanel / DocumentPageActions).
            // {
            //     key: 'rotate',
            //     label: 'Rotate page(s)',
            //     parenthetical: 'Coming soon',
            //     disabled: true,
            //     disabledHint: 'Rotate coming soon',
            //     onSelect: () => {},
            // },
        ];

        return {
            disabled: pageActions.disabled,
            items,
        };
    }, [
        pageActions,
        selectedCount,
        allPages,
        lastSelectedIndex,
        currentPageIndex,
        openDeleteModal,
        openSplitModal,
        openSplitAfterCurrentModal,
        previousDocument,
        nextDocument,
        onMergeAdjacent,
    ]);

    const handleDeleteConfirm = React.useCallback(() => {
        if (!pageActions || !deleteRequest || deleteRequest.pageIds.length === 0) return;
        pageActions.onDeletePages(deleteRequest.pageIds);
        setDeleteRequest(null);
    }, [pageActions, deleteRequest]);

    const splitFirstPageCount = splitRequest
        ? allPages.findIndex(p => p.id === splitRequest.anchorPageId) + 1
        : 0;
    const splitSecondPageCount = splitRequest
        ? Math.max(0, allPages.length - splitFirstPageCount)
        : 0;
    const splitCanSubmit = !!splitRequest
        && splitSecondPageCount > 0
        && splitRequest.firstTitle.trim().length > 0
        && splitRequest.secondTitle.trim().length > 0;

    const handleSplitConfirm = React.useCallback(() => {
        if (!pageActions || !splitRequest || !splitCanSubmit) return;
        pageActions.onSplitAfter(splitRequest.anchorPageId, {
            firstDocTitle: splitRequest.firstTitle.trim(),
            secondDocTitle: splitRequest.secondTitle.trim(),
        });
        setSplitRequest(null);
    }, [pageActions, splitRequest, splitCanSubmit]);

    const closeDeleteModal = React.useCallback(() => setDeleteRequest(null), []);
    const closeSplitModal = React.useCallback(() => setSplitRequest(null), []);

    const handleKeyboardNavigate = React.useCallback(
        (direction: 'next' | 'previous', extend: boolean) => {
            const ordered = filteredPages;
            if (ordered.length === 0) {
                return;
            }
            // Step from the page the viewport is actually on, so arrows pick
            // up from wherever the user scrolled to.
            const currentIndex = ordered.findIndex(page => page.id === currentPageId);
            const baseIndex = currentIndex >= 0 ? currentIndex : 0;
            const delta = direction === 'next' ? 1 : -1;
            const targetIndex = baseIndex + delta;
            if (targetIndex < 0 || targetIndex >= ordered.length) {
                return;
            }
            const targetPage = ordered[targetIndex];
            // Arrow navigation always moves the canvas viewer; shift+arrow
            // additionally extends the checkbox selection through the App-level
            // toggle handler.
            setViewingPageId(targetPage.id);
            setScrollToken(token => token + 1);
            if (extend) {
                onToggleSelect?.(targetPage.id, true);
            }
        },
        [filteredPages, currentPageId, onToggleSelect],
    );

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (deleteRequest || splitRequest) {
                return;
            }
            if (event.defaultPrevented) {
                return;
            }
            const target = event.target as HTMLElement | null;
            if (target) {
                const tagName = target.tagName.toLowerCase();
                if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target.isContentEditable) {
                    return;
                }
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                handleKeyboardNavigate('next', event.shiftKey);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                handleKeyboardNavigate('previous', event.shiftKey);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyboardNavigate, deleteRequest, splitRequest]);

    const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = railWidth;

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const delta = moveEvent.clientX - startX;
            const next = Math.min(Math.max(startWidth + delta, MIN_RAIL_WIDTH), MAX_RAIL_WIDTH);
            setRailWidth(next);
        };

        const handlePointerUp = () => {
            setIsResizing(false);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        setIsResizing(true);
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? 40 : 16;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setRailWidth(prev => Math.max(prev - step, MIN_RAIL_WIDTH));
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            setRailWidth(prev => Math.min(prev + step, MAX_RAIL_WIDTH));
        }
    };

    // Thumbnail click on either a current-doc or adjacent-doc page is now
    // pure navigation — it sets `viewingPageId` and never toggles selection.
    // The token bump makes the continuous stack snap to the page even when
    // the id didn't change.
    const handleThumbnailClick = React.useCallback(
        (pageId: string, _event: DocumentPageRailInteractionEvent) => {
            setViewingPageId(pageId);
            setScrollToken(token => token + 1);
        },
        [],
    );

    // Rail drag-and-drop: translate a drop onto `targetPageId` into a reorder.
    // When the grabbed page is part of the selection, the WHOLE selection moves
    // as one contiguous chunk (ordered by document position); otherwise just the
    // grabbed page moves. The backend `move` action gathers them after the
    // anchor and skips selected pages when resolving it, so the chunk lands
    // intact at the drop point.
    const handleRailReorderDrop = React.useCallback(
        (draggedPageId: string, targetPageId: string, position: 'before' | 'after') => {
            if (!onReorderPages) return;
            const draggedIsSelected = allPages.some(p => p.id === draggedPageId && p.selected);
            const movingIds = draggedIsSelected && orderedSelectedIds.length > 0
                ? orderedSelectedIds
                : [draggedPageId];
            // Dropping onto a member of the moving chunk is a no-op.
            if (movingIds.includes(targetPageId)) return;

            // Capture the undo target BEFORE the move: the page the chunk should
            // land after to restore order (null when it was at the head). Undo
            // re-moves the chunk there. Computed from the pre-move order, which
            // is exactly what `allPages` still holds here.
            const movingSet = new Set(movingIds);
            const undoAfterId = computeUndoAnchor(allPages, movingIds);
            const movedNumbers = allPages
                .filter(p => movingSet.has(p.id))
                .map(p => p.pageNumber);
            const ranges = formatPageRanges(movedNumbers);
            const label = movedNumbers.length === 1
                ? `Moved page ${ranges}`
                : `Moved pages ${ranges}`;

            onReorderPages(movingIds, targetPageId, position);
            if (onUndoReorder) {
                setMovePill(prev => ({
                    label,
                    pageIds: movingIds,
                    undoAfterId,
                    nonce: (prev?.nonce ?? 0) + 1,
                }));
            }
        },
        [onReorderPages, onUndoReorder, allPages, orderedSelectedIds],
    );

    const handleUndoMove = React.useCallback(() => {
        if (!movePill || !onUndoReorder) return;
        onUndoReorder(movePill.pageIds, movePill.undoAfterId);
        setMovePill(null);
    }, [movePill, onUndoReorder]);

    // Build the pill value. If the last commit was invalid we surface that bad
    // text (so the user can re-edit it); otherwise we display the formatted
    // ranges of the actual selection.
    const selectionPillValue = React.useMemo(() => {
        if (invalidPillText != null) return invalidPillText;
        const numbers = allPages.filter(p => p.selected).map(p => p.pageNumber);
        return formatPageRanges(numbers);
    }, [allPages, invalidPillText]);

    const handlePillCommit = React.useCallback(
        (text: string) => {
            if (!onSetSelection) return;
            const trimmed = text.trim();
            if (!trimmed) {
                // Treat empty as "clear and dismiss invalid state".
                setInvalidPillText(null);
                onSetSelection([]);
                return;
            }
            const parsed = parsePageRanges(trimmed, allPages.length);
            if (parsed == null) {
                // Invalid: clear the selection, mark the pill as bad so the
                // user can fix it.
                setInvalidPillText(trimmed);
                onSetSelection([]);
                return;
            }
            // Map page numbers back to IDs (current doc only).
            const byNumber = new Map(allPages.map(p => [p.pageNumber, p.id]));
            const ids = parsed
                .map(n => byNumber.get(n))
                .filter((id): id is string => typeof id === 'string');
            setInvalidPillText(null);
            onSetSelection(ids);
        },
        [allPages, onSetSelection],
    );

    const handlePillClear = React.useCallback(() => {
        setInvalidPillText(null);
        onSetSelection?.([]);
    }, [onSetSelection]);

    const selectionPill: SelectionPillConfig | undefined = React.useMemo(() => {
        // Show the pill if there's either an actual selection or a lingering
        // invalid commit to re-edit.
        if (!onSetSelection) return undefined;
        if (selectionPillValue.length === 0) return undefined;
        return {
            value: selectionPillValue,
            invalid: invalidPillText != null,
            onCommit: handlePillCommit,
            onClear: handlePillClear,
        };
    }, [onSetSelection, selectionPillValue, invalidPillText, handlePillCommit, handlePillClear]);

    // The selection pill moves with the search bar — same shape as the rail
    // used to feed into SearchFilterBar.
    const toolbarSearchPills = React.useMemo(() => {
        if (!selectionPill) return undefined;
        return [
            {
                id: 'selection',
                label: 'Selected',
                value: selectionPill.value,
                editable: true,
                invalid: selectionPill.invalid,
                onCommit: selectionPill.onCommit,
            },
        ];
    }, [selectionPill]);

    const handleRemoveToolbarPill = React.useCallback(() => {
        selectionPill?.onClear();
    }, [selectionPill]);

    // Drag reorder is only coherent when the rail shows the full, contiguous
    // page list. A search or duplicate filter hides pages, so "before/after a
    // visible page" wouldn't map to an unambiguous document position — disable
    // reordering until the filter is cleared.
    const railShowsAllPages =
        displayedPages.length === allPages.length && !duplicateFilterDocId;

    const railProps: DocumentPageRailProps = React.useMemo(
        () => ({
            ...pageRail,
            pages: displayedPages,
            // searchValue is still passed so per-page snippets can highlight matches.
            searchValue: searchQuery ?? '',
            onPageClick: handleThumbnailClick,
            onToggleSelect,
            onReorderDrop: (onReorderPages && railShowsAllPages) ? handleRailReorderDrop : undefined,
            collapsed: railCollapsed,
            onToggleCollapsed: () => setRailCollapsed(c => !c),
            // Reset rail scroll whenever the open document changes.
            scrollResetKey: header.documentId,
            railWidth,
            railMinWidth: MIN_RAIL_WIDTH,
            railMaxWidth: MAX_RAIL_WIDTH,
            // Keep the current viewport page's thumbnail visible in the rail.
            activePageId: currentPageId,
        }),
        [displayedPages, pageRail, searchQuery, handleThumbnailClick, onToggleSelect, onReorderPages, railShowsAllPages, handleRailReorderDrop, railCollapsed, header.documentId, railWidth, currentPageId],
    );

    // Continuous-scroll page set: the rail's filtered ordering, resolved to
    // full canvas pages, so the stack and the rail always show the same set.
    const stackPages = React.useMemo(() => {
        const byId = new Map(pages.map(page => [page.id, page]));
        return filteredPages
            .map(item => byId.get(item.id))
            .filter((page): page is CanvasPage => page != null);
    }, [pages, filteredPages]);

    const handleViewportPageChange = React.useCallback((pageId: string) => {
        setViewportPageId(pageId);
    }, []);

    // Toolbar page indicator: position of the current page within the visible
    // (filtered) set — matches what the rail and the scroll stack show.
    const pageNav = React.useMemo(() => {
        if (filteredPages.length === 0) {
            return undefined;
        }
        const index = filteredPages.findIndex(page => page.id === currentPageId);
        return {
            current: (index >= 0 ? index : 0) + 1,
            total: filteredPages.length,
            onPrevious: () => handleKeyboardNavigate('previous', false),
            onNext: () => handleKeyboardNavigate('next', false),
        };
    }, [filteredPages, currentPageId, handleKeyboardNavigate]);

    // Toolbar prev/next DOCUMENT navigation (moved out of the page rail).
    const docNav = React.useMemo(() => {
        if (!onNavigateToDocument || (!previousDocument && !nextDocument)) {
            return undefined;
        }
        return {
            previousTitle: previousDocument?.title ?? previousDocument?.docId,
            nextTitle: nextDocument?.title ?? nextDocument?.docId,
            onPrevious: previousDocument
                ? () => onNavigateToDocument(previousDocument.docId)
                : undefined,
            onNext: nextDocument
                ? () => onNavigateToDocument(nextDocument.docId)
                : undefined,
        };
    }, [previousDocument, nextDocument, onNavigateToDocument]);

    const toolbarZoom = React.useMemo(() => ({
        value: zoom,
        min: ZOOM_MIN,
        max: ZOOM_MAX,
        onZoomIn: () => zoomBy(ZOOM_STEP),
        onZoomOut: () => zoomBy(-ZOOM_STEP),
        onReset: () => setZoom(1),
    }), [zoom, zoomBy]);

    const formatPageList = (numbers: number[]): string => {
        if (numbers.length === 0) return '';
        if (numbers.length <= 6) {
            return numbers.map(n => `p. ${n}`).join(', ');
        }
        const head = numbers.slice(0, 5).map(n => `p. ${n}`).join(', ');
        return `${head}, … (+${numbers.length - 5} more)`;
    };

    const effectiveRailWidth = railCollapsed ? COLLAPSED_RAIL_WIDTH : railWidth;

    return (
        <Wrapper>
            <DocumentInspectHeader {...header} />
            <DocumentInspectToolbar
                searchValue={searchQuery ?? ''}
                onSearchChange={value => onSearchQueryChange?.(value)}
                searchPills={toolbarSearchPills}
                onRemoveSearchPill={selectionPill ? handleRemoveToolbarPill : undefined}
                status={documentStatus.status}
                pageActions={canvasPageActions}
                viewerMode={viewerMode}
                onViewerModeChange={setViewerMode}
                pageNav={pageNav}
                docNav={docNav}
                zoom={toolbarZoom}
            />
            <MainArea $railWidth={effectiveRailWidth}>
                <RailContainer>
                    <DocumentPageRail {...railProps} />
                </RailContainer>
                <Resizer
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuemin={MIN_RAIL_WIDTH}
                    aria-valuemax={MAX_RAIL_WIDTH}
                    aria-valuenow={Math.round(effectiveRailWidth)}
                    tabIndex={0}
                    onPointerDown={railCollapsed ? undefined : handleResizePointerDown}
                    onKeyDown={railCollapsed ? undefined : handleResizeKeyDown}
                    $dragging={isResizing}
                />
                <ViewerColumn>
                    <CanvasWrapper>
                        <DocumentCanvasPanel
                            page={activePage ?? null}
                            pages={stackPages}
                            activePageId={viewingPageId ?? visibleActivePageId}
                            scrollToken={scrollToken}
                            scrollResetKey={header.documentId}
                            onViewportPageChange={handleViewportPageChange}
                            zoom={zoom}
                            ocrText={activeOcrText}
                            searchQuery={searchQuery}
                            mode={viewerMode}
                            duplicateData={duplicateData}
                            onNavigateToDuplicate={onNavigateToDuplicate}
                            getThumbnailUrl={getThumbnailUrl}
                            previousDocument={previousDocument}
                            nextDocument={nextDocument}
                            onNavigateToDocument={onNavigateToDocument}
                        />
                    </CanvasWrapper>
                </ViewerColumn>
            </MainArea>

            {movePill && onUndoReorder && (
                <MovePillAnchor>
                    <PageMovePill
                        key={movePill.nonce}
                        label={movePill.label}
                        onUndo={handleUndoMove}
                        onExpire={() => setMovePill(null)}
                    />
                </MovePillAnchor>
            )}

            {deleteRequest && (
                <ModalOverlay role="dialog" aria-modal="true" onClick={closeDeleteModal}>
                    <ModalPanel $width="440px" onClick={event => event.stopPropagation()}>
                        <ModalTitle>
                            Delete {deleteRequest.pageIds.length} page{deleteRequest.pageIds.length === 1 ? '' : 's'}?
                        </ModalTitle>
                        <ModalBody>
                            <span>
                                The following page{deleteRequest.pageIds.length === 1 ? '' : 's'} will be removed from{' '}
                                <strong>{header.documentTitle}</strong>:
                            </span>
                            <div style={{ fontWeight: 500, color: '#1f2937' }}>
                                {formatPageList(deleteRequest.pageNumbers)}
                            </div>
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>This action cannot be undone.</span>
                        </ModalBody>
                        <ModalActions>
                            <ModalButton $variant="secondary" type="button" onClick={closeDeleteModal}>Cancel</ModalButton>
                            <ModalButton type="button" $variant="danger" onClick={handleDeleteConfirm}>
                                Delete page{deleteRequest.pageIds.length === 1 ? '' : 's'}
                            </ModalButton>
                        </ModalActions>
                    </ModalPanel>
                </ModalOverlay>
            )}

            {splitRequest && (
                <ModalOverlay role="dialog" aria-modal="true" onClick={closeSplitModal}>
                    <ModalPanel $width="560px" onClick={event => event.stopPropagation()}>
                        <ModalTitle>Split document after p. {splitRequest.anchorPageNumber}</ModalTitle>
                        <ModalBody>
                            {splitSecondPageCount === 0 ? (
                                <span style={{ color: '#b91c1c' }}>
                                    Cannot split — no pages remain after p. {splitRequest.anchorPageNumber}.
                                </span>
                            ) : (
                                <>
                                    <span>
                                        The current document will be split into two. Pages 1–{splitRequest.anchorPageNumber} stay
                                        in the original; pages after p. {splitRequest.anchorPageNumber} move into a new document
                                        inserted immediately after it.
                                    </span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <ModalLabel htmlFor="split-first-title">
                                            First document title ({splitFirstPageCount} page{splitFirstPageCount === 1 ? '' : 's'})
                                        </ModalLabel>
                                        <ModalInput
                                            id="split-first-title"
                                            value={splitRequest.firstTitle}
                                            onChange={event => setSplitRequest(prev => prev ? { ...prev, firstTitle: event.target.value } : prev)}
                                            autoFocus
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <ModalLabel htmlFor="split-second-title">
                                            New document title ({splitSecondPageCount} page{splitSecondPageCount === 1 ? '' : 's'})
                                        </ModalLabel>
                                        <ModalInput
                                            id="split-second-title"
                                            value={splitRequest.secondTitle}
                                            onChange={event => setSplitRequest(prev => prev ? { ...prev, secondTitle: event.target.value } : prev)}
                                        />
                                    </div>
                                </>
                            )}
                        </ModalBody>
                        <ModalActions>
                            <ModalButton $variant="secondary" type="button" onClick={closeSplitModal}>Cancel</ModalButton>
                            <ModalButton
                                type="button"
                                $variant="primary"
                                disabled={!splitCanSubmit}
                                onClick={handleSplitConfirm}
                            >
                                Split document
                            </ModalButton>
                        </ModalActions>
                    </ModalPanel>
                </ModalOverlay>
            )}
        </Wrapper>
    );
};
